import cn from "classnames";
import React, { useMemo, useState } from "react";
import PhoneInput from "react-phone-number-input";
import {
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Row,
} from "reactstrap";
import { useIntl } from "react-intl";
import { useCompanyCore } from "../../../cores/company";
import { FromFeedback2 } from "../FromFeedback2";
import { useMeCore } from "../../../cores/me";
import { useGlobalCore } from "../../../cores/globals";
import { LabelWembii } from "../LabelWembii";
import { useRouterCore } from "../../../cores/router";

interface Props {
  onSubmit?: () => void;
  asReseller?: boolean;
  autoSelectOnCreation?: boolean;
  /** Primera empresa / onboarding: solo nombre; el resto se completa en Editar empresa. */
  minimal?: boolean;
  /** centerDialog: layout para modal centrado (MeCompanies). */
  variant?: "sidePanel" | "centerDialog";
}

export default function CreateCompanyForm(props: Props) {
  const intl = useIntl();
  const {
    onSubmit,
    asReseller = false,
    autoSelectOnCreation = false,
    minimal = false,
    variant = "sidePanel",
  } = props;
  const isDialog = variant === "centerDialog";
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [phone, setPhone] = useState("");
  const { postCompany } = useCompanyCore();
  const { goToSocial } = useRouterCore();
  const { selectCompany, getMeCompanies } = useMeCore();
  const { reset } = useGlobalCore();

  const onFormSubmit = async (e: any) => {
    e.preventDefault();

    const company = await postCompany(
      name.trim(),
      description,
      website,
      phone,
      country,
      city,
      false,
      asReseller
    );

    setName("");
    setDescription("");
    setWebsite("");
    setCountry("");
    setCity("");
    setPhone("");
    onSubmit?.();
    
    if (company && autoSelectOnCreation) {
      reset();
      // Lista del usuario: GET me/companies — GET /company es solo admin/reseller
      await getMeCompanies();
      goToSocial();
      selectCompany(company.company.id);
    }
  };

  const isFieldToLong = (field: string, length: number) =>
    field.length > length;
  const fieldLengthFeedback = (field: string, length: number) => (
    <FromFeedback2
      show={isFieldToLong(field, length)}
      message={intl.formatMessage(
        { id: "app.adminCompanies.error1" },
        { length }
      )}
    />
  );

  const isNameInvalid = useMemo(() => isFieldToLong(name, 35), [name]);
  const isDescriptionInvalid = useMemo(
    () => isFieldToLong(description, 100),
    [description]
  );
  const isWebsiteInvalid = useMemo(() => isFieldToLong(website, 100), [website]);
  const isCountryInvalid = useMemo(() => isFieldToLong(country, 35), [country]);
  const isCityInvalid = useMemo(() => isFieldToLong(city, 35), [city]);

  const disabled = useMemo(() => {
    if (!name.trim() || isNameInvalid) return true;
    if (minimal) return false;
    return (
      isDescriptionInvalid ||
      isCityInvalid ||
      isWebsiteInvalid ||
      isCountryInvalid
    );
  }, [
    name,
    minimal,
    isNameInvalid,
    isDescriptionInvalid,
    isCityInvalid,
    isWebsiteInvalid,
    isCountryInvalid,
  ]);

  const titleClass = isDialog
    ? minimal
      ? "text-center mb-2 fw-semibold create-company-modal__title"
      : "text-center mb-3 fw-semibold create-company-modal__title"
    : minimal
    ? "h2 text-center mb-2"
    : "h2 text-center mb-4";

  const TitleTag = isDialog ? "h3" : "h2";

  return (
    <Container
      className={cn("create-company-modal", "px-2", {
        "h-100": !isDialog,
        "create-company-modal--dialog": isDialog,
      })}
      fluid
    >
      <Row className={cn({ "h-100": !isDialog })}>
        <Col xs="12">
          <div
            className={cn("d-flex flex-column", {
              "h-100 justify-content-center py-5": !isDialog,
              "py-3 px-2 px-sm-3": isDialog,
            })}
          >
            <TitleTag className={titleClass}>
              {intl.formatMessage({
                id: minimal
                  ? "app.company.minimalCreateTitle"
                  : "app.adminCompanies.title",
              })}
            </TitleTag>
            {minimal ? (
              <p
                className={cn(
                  "text-center small mb-4 px-1",
                  isDialog
                    ? "create-company-modal__hint"
                    : "text-muted"
                )}
              >
                {intl.formatMessage({ id: "app.company.minimalCreateHint" })}
              </p>
            ) : null}
            <Form
              className={cn({ "px-1": isDialog, "px-2": !isDialog })}
              onSubmit={onFormSubmit}
            >
              <FormGroup>
                <LabelWembii forid="company-name" required>
                  {intl.formatMessage({ id: "app.nameTxt" })}
                </LabelWembii>
                <Input
                  type="text"
                  invalid={isNameInvalid}
                  name="name"
                  id="company-name"
                  placeholder={intl.formatMessage({ id: "app.nameTxt" })}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                {fieldLengthFeedback(name, 35)}
              </FormGroup>
              {!minimal && (
                <>
                  <FormGroup>
                    <LabelWembii forid="company-description">
                      {intl.formatMessage({
                        id: "app.adminCompanies.descriptionTxt",
                      })}
                    </LabelWembii>
                    <Input
                      type="text"
                      name="description"
                      id="company-description"
                      placeholder={intl.formatMessage({
                        id: "app.adminCompanies.descriptionTxt",
                      })}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      invalid={isDescriptionInvalid}
                    />

                    {fieldLengthFeedback(description, 100)}
                  </FormGroup>
                  <FormGroup>
                    <LabelWembii forid="company-webSite">
                      {intl.formatMessage({ id: "app.adminCompanies.websiteTxt" })}
                    </LabelWembii>
                    <Input
                      type="text"
                      name="webSite"
                      id="company-webSite"
                      placeholder={intl.formatMessage({
                        id: "app.adminCompanies.websiteTxt",
                      })}
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      invalid={isWebsiteInvalid}
                    />

                    {fieldLengthFeedback(website, 100)}
                  </FormGroup>
                  <FormGroup>
                    <LabelWembii forid="company-phone">
                      {intl.formatMessage({ id: "app.phoneTxt" })}
                    </LabelWembii>
                    <PhoneInput
                      name="phone"
                      id="company-phone"
                      placeholder={intl.formatMessage({ id: "app.phoneTxt" })}
                      value={phone}
                      onChange={(v) => setPhone(v || "")}
                    />
                  </FormGroup>
                  <FormGroup>
                    <LabelWembii forid="company-country">
                      {intl.formatMessage({ id: "app.adminCompanies.countryTxt" })}
                    </LabelWembii>
                    <Input
                      type="text"
                      name="country"
                      id="company-country"
                      placeholder={intl.formatMessage({
                        id: "app.adminCompanies.countryTxt",
                      })}
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      invalid={isCountryInvalid}
                    />
                    {fieldLengthFeedback(country, 35)}
                  </FormGroup>
                  <FormGroup>
                    <LabelWembii forid="company-city">
                      {intl.formatMessage({ id: "app.adminCompanies.cityTxt" })}
                    </LabelWembii>
                    <Input
                      type="text"
                      name="city"
                      id="company-city"
                      placeholder={intl.formatMessage({
                        id: "app.adminCompanies.cityTxt",
                      })}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      invalid={isCityInvalid}
                    />
                    {fieldLengthFeedback(city, 35)}
                  </FormGroup>
                </>
              )}
              <Button
                color="primary"
                block
                disabled={!!disabled}
                className={cn(
                  isDialog && "rounded-pill py-2 fw-semibold mt-1 create-company-modal__submit"
                )}
              >
                {intl.formatMessage({ id: "app.adminCompanies.createCompany" })}
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
